/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import { useAgencyBranding } from '../../branding';
import {
	Container,
	Grid,
	Typography,
	Card,
	CardContent,
	CardActions,
	Button,
	Theme,
	Box,
	Chip,
	Switch,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import Logo from './celeste-asistencia-en-viajes-logo.webp';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import SecurityIcon from '@mui/icons-material/Security';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { CountryCodeEnum } from 'features/common/types';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

interface SeguroAsistenciaProps {
	ntsQuantity: number;
	country: CountryCodeEnum;
	/** Tasa de conversión de 1 USD a COP (por ejemplo, 1 USD = 4800 COP) */
	exchangeRate?: number;
	isExternal?: boolean;
	onAssistanceSelectionChange?: (selection: { name: string; totalUSD: number } | null) => void;
	totalPassengers?: number;
}

const products = [
	{
		name: 'Plan Básico',
		attributes: [
			'Asistencia médica x accidente o enfermedad hasta USD 40.000',
			'Enfermedad preexistente hasta USD 7.000',
			'Cancelación de viaje hasta USD 800',
			'Gastos por vuelo demorado hasta USD 120',
			'Gastos por demora de equipaje hasta USD 150',
		],
		dailyPrice: {
			[CountryCodeEnum.URUGUAY]: 5,
			[CountryCodeEnum.PARAGUAY]: 5,
			[CountryCodeEnum.COLOMBIA]: 5,
			[CountryCodeEnum.BOLIVIA]: 6,
			[CountryCodeEnum.TN_ITAU_PY]: 5,
		},
		pdfLink: '/Producto_basico_Celeste_Asistencia_en_viajes.pdf',
	},
	{
		name: 'Plan Estándar',
		attributes: [
			'Asistencia médica x accidente o enfermedad hasta USD 60.000',
			'Enfermedad preexistente hasta USD 7.000',
			'Cancelación de viaje hasta USD 1.000',
			'Gastos por vuelo demorado hasta USD 120',
			'Gastos por demora de equipaje hasta USD 300',
		],
		dailyPrice: {
			[CountryCodeEnum.URUGUAY]: 6,
			[CountryCodeEnum.PARAGUAY]: 6,
			[CountryCodeEnum.COLOMBIA]: 6,
			[CountryCodeEnum.BOLIVIA]: 8,
			[CountryCodeEnum.TN_ITAU_PY]: 6,
		},
		pdfLink: '/Producto_estandar_Celeste_asistencia_en_viajes.pdf',
	},
	{
		name: 'Plan Premium',
		attributes: [
			'Asistencia médica x accidente o enfermedad hasta USD 80.000',
			'Enfermedad preexistente hasta USD 15.000',
			'Cancelación de viaje hasta USD 2.000',
			'Gastos por vuelo demorado hasta USD 450',
			'Gastos por demora de equipaje hasta USD 120',
		],
		dailyPrice: {
			[CountryCodeEnum.URUGUAY]: 8,
			[CountryCodeEnum.PARAGUAY]: 8,
			[CountryCodeEnum.COLOMBIA]: 8,
			[CountryCodeEnum.BOLIVIA]: 10,
			[CountryCodeEnum.TN_ITAU_PY]: 8,
		},
		pdfLink: '/Producto_premium_Celeste_asistencia_en_viajes.pdf',
	},
];

const useStyles = makeStyles((theme: Theme) => ({
	paper: {
		marginTop: theme.spacing(4),
		marginBottom: theme.spacing(4),
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
	},
	logoContainer: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-end',
	},
	logo: {
		maxHeight: 28,
		[theme.breakpoints.down('md')]: {
			maxHeight: 22,
		},
	},
	highlights: {
		marginBottom: theme.spacing(2),
		marginLeft: theme.spacing(1),
		marginRight: theme.spacing(1),
	},
	cardsWrapper: {
		display: 'flex',
		overflowX: 'auto',
		[theme.breakpoints.up('md')]: {
			overflowX: 'hidden',
			justifyContent: 'space-between',
		},
	},
	card: {
		flex: '0 0 auto',
		width: '86%',
		marginRight: theme.spacing(1),
		[theme.breakpoints.up('md')]: {
			width: `calc((99% / 3) - ${theme.spacing(2)})`,
		},
	},
	button: {
		marginTop: theme.spacing(1),
	},
	attributesList: {
		'& li': {
			marginLeft: '16px',
		},
	},
}));

const SeguroAsistencia: React.FC<SeguroAsistenciaProps> = ({
	ntsQuantity,
	country,
	exchangeRate = 0,
	isExternal,
	onAssistanceSelectionChange,
	totalPassengers = 1,
}) => {
	const branding = useAgencyBranding(isExternal);
	const classes = useStyles();
	const totalDays = ntsQuantity + 1;
	const [selectedPlanIndex, setSelectedPlanIndex] = useState<number | null>(null);
	const sectionTitleSx = {
		marginBottom: '10px',
		width: '100%',
		background: branding.headerGradient,
		paddingLeft: '18px',
		paddingRight: '18px',
		paddingTop: '10px',
		paddingBottom: '10px',
		borderRadius: '10px',
		fontSize: '1rem',
		fontWeight: 700,
		color: 'white',
		boxShadow: '0 8px 20px rgba(49, 25, 75, 0.16)',
	};
	const handleTogglePlan = (index: number) => {
		setSelectedPlanIndex((current) => {
			const next = current === index ? null : index;
			const product = next !== null ? products[next] : null;
			const dailyPriceUSD = product ? product.dailyPrice[country] ?? 10 : 0;
			onAssistanceSelectionChange?.(
				product
					? {
							name: product.name,
							totalUSD: dailyPriceUSD * totalDays * totalPassengers,
					  }
					: null,
			);
			return next;
		});
	};

	return (
		<Container component="main" maxWidth="md">
			<div className={classes.paper}>
				<Grid container spacing={1}>
					<Grid item xs={12}>
						<Typography component="h1" variant="h6" sx={sectionTitleSx}>
							Seguro y asistencia en viaje:
						</Typography>
					</Grid>
					<Grid item xs={12}>
						<Typography
							variant="body2"
							color="text.secondary"
							fontWeight={500}
							sx={{
								px: 1,
								pb: 1,
								borderLeft: '3px solid',
								borderColor: 'secondary.main',
								ml: 0.5,
								mb: 1,
							}}
						>
							Podés sumar asistencia en viaje para proteger al grupo ante imprevistos médicos, demoras o cancelaciones
							según el plan elegido.
						</Typography>
					</Grid>
					<Grid item xs={12}>
						<Card
							sx={{
								p: 1.5,
								borderRadius: '10px',
								border: '1px solid rgba(49, 25, 75, 0.10)',
								boxShadow: '0 6px 18px rgba(49, 25, 75, 0.08)',
							}}
						>
							<Box display="flex" alignItems="center" justifyContent="space-between" gap={1.5} mb={1}>
								<Box>
									<Typography variant="body2" fontWeight={800} color="primary">
										Viajá con respaldo antes y durante tu viaje
									</Typography>
									<Typography variant="caption" color="text.secondary" lineHeight={1.3} display="block">
										Servicio brindado por Celeste Asistencia en Viajes.
									</Typography>
								</Box>
								<Box className={classes.logoContainer}>
									<img src={Logo} alt="Celeste asistencia en viajes" className={classes.logo} />
								</Box>
							</Box>
							<Box display="flex" flexWrap="wrap" gap={0.75}>
								<Chip
									size="small"
									icon={<MedicalServicesIcon color="secondary" />}
									label="Asistencia médica"
									sx={{ borderRadius: '999px', fontWeight: 700, bgcolor: 'rgba(49, 25, 75, 0.045)' }}
								/>
								<Chip
									size="small"
									icon={<SecurityIcon color="secondary" />}
									label="Protección del viaje"
									sx={{ borderRadius: '999px', fontWeight: 700, bgcolor: 'rgba(49, 25, 75, 0.045)' }}
								/>
								<Chip
									size="small"
									icon={<AccessTimeIcon color="secondary" />}
									label="Atención 24/7"
									sx={{ borderRadius: '999px', fontWeight: 700, bgcolor: 'rgba(49, 25, 75, 0.045)' }}
								/>
							</Box>
						</Card>
					</Grid>
					<Grid item xs={12}>
						<div className={classes.cardsWrapper}>
							{products.map((product, index) => {
								const selected = selectedPlanIndex === index;
								// Precio base en USD de acuerdo al país
								const dailyPriceUSD = product.dailyPrice[country] ?? 10;

								// Decidimos si mostramos en USD o convertimos a COP
								let dailyPriceDisplay: string;
								let totalPriceDisplay: string;

								if (country === CountryCodeEnum.COLOMBIA) {
									// Convertir a COP
									const dailyPriceCOP = Math.round(dailyPriceUSD * exchangeRate);
									const totalPriceCOP = dailyPriceCOP * totalDays;

									// Formatear con separador de miles (opcional: "es-CO")
									dailyPriceDisplay = `COP ${dailyPriceCOP.toLocaleString('es-CO')}`;
									totalPriceDisplay = `COP ${totalPriceCOP.toLocaleString('es-CO')}`;
								} else {
									// Mostrar en USD
									const totalUSD = dailyPriceUSD * totalDays;
									dailyPriceDisplay = `USD ${dailyPriceUSD}`;
									totalPriceDisplay = `USD ${totalUSD}`;
								}

								return (
									<Card
										key={index}
										className={classes.card}
										sx={{
											borderRadius: '10px',
											border: selected ? '2px solid' : '1px solid rgba(49, 25, 75, 0.10)',
											borderColor: selected ? 'secondary.main' : undefined,
											boxShadow: selected ? '0 10px 26px rgba(237, 102, 2, 0.16)' : '0 5px 16px rgba(49, 25, 75, 0.08)',
											overflow: 'hidden',
										}}
									>
										<CardContent sx={{ p: 1.5 }}>
											<Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
												<Box>
													<Typography variant="h6" color="primary" fontSize="1rem" fontWeight={800} lineHeight={1.15}>
														{product.name}
													</Typography>
													<Chip
														size="small"
														label={`${totalDays} días`}
														sx={{
															mt: 0.75,
															height: 22,
															borderRadius: '999px',
															fontSize: 11,
															fontWeight: 800,
															bgcolor: 'rgba(49, 25, 75, 0.06)',
														}}
													/>
												</Box>
												<Box textAlign="right">
													<Switch color="secondary" checked={selected} onChange={() => handleTogglePlan(index)} />
													<Typography
														variant="caption"
														color={selected ? 'secondary' : 'text.secondary'}
														fontWeight={800}
														display="block"
													>
														{selected ? 'Agregado' : 'Agregar'}
													</Typography>
												</Box>
											</Box>
											<Box display="grid" gap={0.65} mt={1.25}>
												{product.attributes.slice(0, 3).map((attr, i) => (
													<Box key={i} display="flex" gap={0.75} alignItems="flex-start">
														<CheckCircleIcon color="secondary" sx={{ fontSize: 15, mt: 0.15 }} />
														<Typography variant="caption" color="text.secondary" lineHeight={1.25}>
															{attr}
														</Typography>
													</Box>
												))}
											</Box>
											<Box display="flex" alignItems="center" justifyContent="space-between" mt={2}>
												<Typography variant="caption" color="textSecondary">
													Por día p/pax:
												</Typography>
												<Typography variant="body2" color="secondary" fontWeight={800} textAlign="right">
													<strong>{dailyPriceDisplay}</strong>
												</Typography>
											</Box>
											<Box display="flex" alignItems="center" justifyContent="space-between">
												<Typography variant="caption" color="textSecondary">
													Total ({totalDays} días) p/pax:
												</Typography>
												<Typography variant="body2" color="secondary" fontWeight={800} textAlign="right">
													<strong>{totalPriceDisplay}</strong>
												</Typography>
											</Box>
										</CardContent>
										<CardActions sx={{ justifyContent: 'end', px: 1.5, pt: 0, pb: 1.25 }}>
											<Button
												size="small"
												variant="text"
												color="primary"
												className={classes.button}
												href={product.pdfLink}
												target="_blank"
												rel="noopener noreferrer"
												endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
												sx={{
													borderRadius: '999px',
													textTransform: 'none',
													fontWeight: 800,
													fontSize: 12,
													px: 1,
													py: 0.25,
													color: 'primary.main',
													backgroundColor: 'rgba(49, 25, 75, 0.045)',
													'&:hover': {
														backgroundColor: 'rgba(49, 25, 75, 0.075)',
													},
												}}
											>
												Ver condiciones
											</Button>
										</CardActions>
									</Card>
								);
							})}
						</div>
					</Grid>
				</Grid>
			</div>
		</Container>
	);
};

export default SeguroAsistencia;
